////////bar chart
import React, { useEffect, useMemo } from "react";
import { observer } from "mobx-react";
import { reaction } from "mobx";
//styles
import styles from "../../styles/dashboard.module.css";
//loading
import loadingSvg from "../../../Assets/images/tube-spinner.svg";
import { BarChart } from "@mui/x-charts/BarChart";
import { axisClasses } from "@mui/x-charts/ChartsAxis";
import dashboardStore from "../../store/dashboardStore";
import { formatNumber } from "../../utils/tools";
import {
  useItemTooltip,
  ChartsTooltipContainer,
} from "@mui/x-charts/ChartsTooltip";
//翻譯
import { useTranslation } from "react-i18next";

const ISO_LABELS = [
  "1.1",
  "1.2",
  "1.3",
  "1.4",
  "1.5",
  "2.1",
  "2.2",
  "3.1",
  "3.2",
  "3.3",
  "3.4",
  "3.5",
  "4.1",
  "4.2",
  "4.3",
  "4.4",
  "4.5",
  "5.1",
  "5.2",
  "5.3",
  "5.4",
  "6.1",
];

const GHG_LABELS = [
  "1.1",
  "1.2",
  "1.3",
  "1.4",
  "1.5",
  "2.1",
  "2.2",
  "2.3",
  "2.4",
  // 利用 Array.from 快速產生 3.1 ~ 3.15，避免手寫
  ...Array.from({ length: 15 }, (_, i) => `3.${i + 1}`),
];

const OGGEbyCategory = observer(() => {
  const { t } = useTranslation("ActivityMain");

  useEffect(() => {
    const dispose = reaction(
      () => [
        dashboardStore.selectedCom,
        dashboardStore.selectedYear,
        dashboardStore.selectedMonth,
        dashboardStore.gwpId,
        dashboardStore.unit,
        dashboardStore.standardId,
      ],
      () => {
        if (
          !dashboardStore.selectedCom ||
          dashboardStore.selectedCom.length === 0
        )
          return;

        if (!dashboardStore.selectedYear) {
          return;
        }

        dashboardStore.getBarChartData();
      },
      {
        fireImmediately: true,
      },
    );

    return () => dispose();
  }, []);

  const xLabels = dashboardStore.standardId === 1 ? ISO_LABELS : GHG_LABELS;

  const barData = xLabels.map((label) => {
    const key = label.replace(".", "_");
    return dashboardStore.barChartData[key]?.value ?? 0;
  });

  const percentageData = xLabels.map((label) => {
    const key = label.replace(".", "_");
    return dashboardStore.barChartData[key]?.percentage ?? 0;
  });

  //判斷Value是不是都是0
  const isAllZero = barData.length > 0 && barData.every((value) => value === 0);

  //判斷目前單位
  const unitLabel =
    dashboardStore.unit === "20"
      ? "kgCO2e"
      : dashboardStore.unit === "21"
        ? "tCO2e"
        : "";

  const currentColors = useMemo(() => {
    return xLabels.map((label) => {
      const scopeIndex = parseInt(label.split(".")[0]) - 1; // "1.1" -> 0, "2.1" -> 1
      // 防止索引超出 themeColor 範圍
      const safeIndex = Math.min(
        scopeIndex,
        dashboardStore.themeColor.length - 1,
      );
      return dashboardStore.themeColor[safeIndex];
    });
  }, [xLabels, dashboardStore.themeColor]);

  // //客製化tooltip
  // const CustomItemTooltipContent = ({ itemData }) => {
  //   // 確保 itemData 不是直接渲染對象
  //   if (
  //     !itemData ||
  //     (typeof itemData === "object" && !React.isValidElement(itemData))
  //   ) {
  //     // 提取需要的數據
  //     let label = "";
  //     let value = 0;
  //     let percentage = 0;
  //     let color = "#ccc";

  //     try {
  //       // 嘗試從 itemData 獲取正確的值
  //       if (itemData.dataIndex !== undefined) {
  //         const index = itemData.dataIndex;
  //         label = xLabels[index] || "";
  //         value = barData[index] || 0;
  //         percentage = percentageData[index] || 0;
  //         color = currentColors[index] || "#ccc";
  //       }
  //     } catch (error) {
  //       console.error("Error extracting tooltip data:", error);
  //     }

  //     return (
  //       <div className={styles.tooltip}>
  //         <table className={styles.table}>
  //           <tbody>
  //             <tr>
  //               <td>{t("Category")}</td>
  //               <td className={styles.right}>
  //                 <span
  //                   className={styles.colorBox}
  //                   style={{ backgroundColor: color }}
  //                 />
  //                 {label}
  //               </td>
  //             </tr>
  //             <tr>
  //               <td>{t("Emission Value")}</td>
  //               <td className={styles.right}>{value + " " + unitLabel}</td>
  //             </tr>

  //             <tr>
  //               <td>{t("Total Emission Percentage")}</td>
  //               <td className={styles.right}>{percentage} %</td>
  //             </tr>
  //           </tbody>
  //         </table>
  //       </div>
  //     );
  //   }

  //   // 如果 itemData 是一個有效的 React 元素，直接返回它
  //   return itemData;
  // };
  const CustomTooltip = () => {
    const tooltipData = useItemTooltip();

    if (!tooltipData) return null;

    const { identifier, color, label, value, formattedValue } = tooltipData;
    const index = identifier?.dataIndex;

    const displayLabel = xLabels[index] ?? "";
    const displayValue = barData[index] ?? 0;
    const displayPercentage = percentageData[index] ?? 0;
    const displayColor = currentColors[index] ?? "#ccc";

    return (
      <ChartsTooltipContainer trigger="item">
        <div className={styles.tooltip}>
          <table className={styles.table}>
            <tbody>
              <tr>
                <td>{t("Category")}</td>
                <td className={styles.right}>
                  <span
                    className={styles.colorBox}
                    style={{ backgroundColor: displayColor }}
                  />
                  {displayLabel}
                </td>
              </tr>
              <tr>
                <td>{t("Emission Value")}</td>
                <td className={styles.right}>
                  {Number(displayValue).toLocaleString()} {unitLabel}
                </td>
              </tr>
              <tr>
                <td>{t("Total Emission Percentage")}</td>
                <td className={styles.right}>{displayPercentage} %</td>
              </tr>
            </tbody>
          </table>
        </div>
      </ChartsTooltipContainer>
    );
  };

  return (
    <div className={`${styles.chartContent} ${styles.grid_8}`}>
      <div className={styles.chartTitle}>
        {t("Organizational Greenhouse Gas Emission by Categories")}
      </div>
      {/* <div className={styles.chartBody}> */}
      <div className={styles.chart}>
        {/**loading page */}
        {dashboardStore.barChartDataLoading ? (
          <div className={styles.loading}>
            <img
              className={styles.loadingImg}
              src={loadingSvg}
              alt="loadingSvg"
            />
          </div>
        ) : isAllZero ? (
          <div className={styles.noData}>{t("No data")}!</div>
        ) : (
          <BarChart
            // width={700}
            height={250}
            series={[
              {
                data: barData,
                percentage: percentageData,
                label: "value",
                id: "ValueID",
                minBarSize: 10,
              },
            ]}
            borderRadius={5}
            xAxis={[
              {
                data: xLabels,
                scaleType: "band",
                categoryGapRatio: 0.5,
                colorMap: {
                  type: "ordinal",
                  colors: currentColors,
                  // dashboardStore.groupBy === "scope" ? colorSet : colorSet6,
                },
              },
            ]}
            yAxis={[
              {
                label: `${t("Total Emissions")} (${unitLabel})`,
                // scaleType: "symlog",
                width: 80,
                valueFormatter: (value) => formatNumber(value),
              },
            ]}
            slots={{
              tooltip: CustomTooltip, // ✅ 正確位置
            }}
            slotProps={{
              legend: {
                hidden: true,
              },
              tooltip: {
                trigger: "item",
              },
            }}
            margin={{ left: 20, right: 20, top: 15, bottom: 5 }}
            sx={{
              [`.${axisClasses.left} .${axisClasses.label}`]: {
                transform: "translate(-20px, 0)",
              },
              ".MuiChartsAxis-label tspan": {
                fontSize: 14, // 或 '12px'
              },
            }}
          />
        )}
      </div>
    </div>
  );
});

export default OGGEbyCategory;
